/**
 * Audit Log page per COMP-007 Story 7 & Story 8.
 * Admin + Auditor: filter, search, expand, export audit events.
 * Default sort: Timestamp newest first (BL-008).
 */

import { useState, useCallback } from "react";
import { Download, FileText, Filter, ChevronDown, ChevronRight, X, Radio, Pause } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "@/components/sc/glass-card";
import { StatusBadge } from "@/components/sc/status-badge";
import { EmptyState } from "@/components/sc/empty-state";
import { DataTable, type Column, type SortState } from "@/components/sc/data-table";
import { useAuditLog, useAuditExport } from "@/hooks/use-audit";
import { AUDIT_EVENT_TYPES, type AuditLogEntry } from "@/types";

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString();
}

function formatInitials(name: string | null): string {
  if (!name) return "SYS";
  return name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// Severity filter options — ordered from lowest to highest
const SEVERITY_OPTIONS = ["INFO", "WARNING", "CRITICAL"] as const;

// Event types grouped by category, each group sorted alphabetically
const EVENT_TYPE_GROUPS: { label: string; types: string[] }[] = (() => {
  const groups: Record<string, string[]> = {
    "Access Reviews": [],
    Authentication: [],
    "Cache Management": [],
    "Customer Config": [],
    "Exception Groups": [],
    "GDAP Relationships": [],
    Notifications: [],
    "PIM Sessions": [],
    Settings: [],
    "Technician Access": [],
  };

  for (const t of AUDIT_EVENT_TYPES) {
    if (t.startsWith("AUTH_") || t === "USER_LOGGED_OUT") groups["Authentication"]!.push(t);
    else if (t.startsWith("CACHE_") || t === "WEBHOOK_AUTH_FAILED") groups["Cache Management"]!.push(t);
    else if (t.startsWith("CUSTOMER_CONFIG") || t.startsWith("CUSTOMER_ACCESS_TYPE") || t === "CUSTOMER_ONBOARDED") groups["Customer Config"]!.push(t);
    else if (t.startsWith("CUSTOMER_ACCESS_")) groups["PIM Sessions"]!.push(t);
    else if (t.startsWith("EXCEPTION_GROUP_")) groups["Exception Groups"]!.push(t);
    else if (t.startsWith("GDAP_")) groups["GDAP Relationships"]!.push(t);
    else if (t.startsWith("NOTIFICATION_")) groups["Notifications"]!.push(t);
    else if (t.startsWith("PIM_")) groups["PIM Sessions"]!.push(t);
    else if (t.startsWith("ACCESS_CHECK") || t.startsWith("TECHNICIAN_")) groups["Technician Access"]!.push(t);
    else if (t.startsWith("ACCESS_REVIEW")) groups["Access Reviews"]!.push(t);
    else if (t === "SETTINGS_CHANGED" || t === "RETENTION_POLICY_CHANGED" || t === "PORTAL_SETTINGS_CHANGED" || t === "ENTRA_GROUP_CREATED" || t === "APPROVAL_POLICIES_CHANGED") groups["Settings"]!.push(t);
    else groups["Settings"]!.push(t);
  }

  // Sort each group's types alphabetically
  for (const g of Object.values(groups)) g.sort();

  // Return only non-empty groups, sorted by label
  return Object.entries(groups)
    .filter(([, types]) => types.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([label, types]) => ({ label, types }));
})();

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState<SortState>({
    column: "timestamp",
    direction: "desc",
  });
  const [liveUpdates, setLiveUpdates] = useState(true);

  // Filter state
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [eventType, setEventType] = useState("");
  const [severity, setSeverity] = useState("");
  const [actor, setActor] = useState("");
  const [target, setTarget] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Expanded row state
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Export state
  const [exportFormat, setExportFormat] = useState<"csv" | "json">("csv");

  const auditQuery = useAuditLog({
    page,
    pageSize,
    from: fromDate || undefined,
    to: toDate || undefined,
    eventType: eventType || undefined,
    severity: severity || undefined,
    actor: actor || undefined,
    target: target || undefined,
    sortColumn: sort.column || undefined,
    sortDirection: sort.direction || undefined,
    liveUpdates,
  });

  const exportQuery = useAuditExport({
    format: exportFormat,
    from: fromDate || undefined,
    to: toDate || undefined,
    eventType: eventType || undefined,
  });

  const events = auditQuery.data?.items ?? [];
  const total = auditQuery.data?.total ?? 0;

  const handleExport = useCallback(
    (format: "csv" | "json") => {
      setExportFormat(format);

      const fileName = `audit-export-${fromDate || "all"}-${toDate || "all"}.${format}`;

      // Use refetch to trigger the disabled query
      exportQuery
        .refetch()
        .then((result) => {
          if (result.data) {
            const blob =
              result.data instanceof Blob
                ? result.data
                : new Blob([JSON.stringify(result.data)], {
                    type: format === "csv" ? "text/csv" : "application/json",
                  });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`Exported audit log as ${format.toUpperCase()}`);
          }
        })
        .catch(() => {
          toast.error("Export failed. Try narrowing your date range.");
        });
    },
    [fromDate, toDate, eventType, exportQuery],
  );

  // Active filter chips
  const activeFilters: { label: string; key: string; onRemove: () => void }[] = [];
  if (eventType)
    activeFilters.push({
      label: `Event: ${eventType}`,
      key: "eventType",
      onRemove: () => {
        setEventType("");
        setPage(1);
      },
    });
  if (severity)
    activeFilters.push({
      label: `Severity: ${severity}`,
      key: "severity",
      onRemove: () => {
        setSeverity("");
        setPage(1);
      },
    });
  if (actor)
    activeFilters.push({
      label: `Actor: ${actor}`,
      key: "actor",
      onRemove: () => {
        setActor("");
        setPage(1);
      },
    });
  if (target)
    activeFilters.push({
      label: `Target: ${target}`,
      key: "target",
      onRemove: () => {
        setTarget("");
        setPage(1);
      },
    });

  const COLUMNS: Column<AuditLogEntry>[] = [
    {
      key: "expand",
      header: "",
      className: "w-8",
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpandedId(expandedId === row.id ? null : row.id);
          }}
          className="text-sc-text-muted hover:text-sc-text"
          aria-label={expandedId === row.id ? "Collapse row" : "Expand row"}
        >
          {expandedId === row.id ? (
            <ChevronDown size={16} />
          ) : (
            <ChevronRight size={16} />
          )}
        </button>
      ),
    },
    {
      key: "timestamp",
      header: "Timestamp",
      sortable: true,
      className: "min-w-[160px]",
      render: (row) => (
        <span className="text-body-sm text-sc-text whitespace-nowrap">
          {formatTimestamp(row.timestamp)}
        </span>
      ),
    },
    {
      key: "eventType",
      header: "Event Type",
      sortable: true,
      className: "max-w-[250px]",
      render: (row) => (
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-badge font-medium bg-gray-100 text-gray-700 dark:bg-gray-800/50 dark:text-gray-300"
          title={row.eventType}
        >
          {row.eventType.replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "actorDisplayName",
      header: "Actor",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-sc-primary/10 text-sc-primary text-[10px] font-bold shrink-0">
            {formatInitials(row.actorDisplayName)}
          </span>
          <span
            className="text-body-sm text-sc-text truncate max-w-[150px]"
            title={row.actorDisplayName ?? "System"}
          >
            {row.actorDisplayName ?? "System"}
          </span>
        </div>
      ),
    },
    {
      key: "targetResourceName",
      header: "Target",
      className: "max-w-[200px]",
      render: (row) => (
        <span
          className="text-body-sm text-sc-text truncate block"
          title={row.targetResourceName}
        >
          {row.targetResourceName}
        </span>
      ),
    },
    {
      key: "severity",
      header: "Severity",
      sortable: true,
      render: (row) => (
        <StatusBadge status={row.severity} category="severity" />
      ),
    },
    {
      key: "correlationId",
      header: "Correlation",
      render: (row) => (
        <span
          className="font-mono text-label text-sc-text-muted truncate block max-w-[100px]"
          title={row.correlationId}
        >
          {row.correlationId.slice(0, 8)}...
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Actions bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-btn text-body-sm font-medium transition-colors ${
              showFilters || activeFilters.length > 0
                ? "bg-sc-primary text-white"
                : "text-sc-text-muted hover:text-sc-text hover:bg-sc-surface-hover border border-sc-border"
            }`}
          >
            <Filter size={14} />
            Filters
            {activeFilters.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center h-4 w-4 rounded-full bg-white/20 text-[10px]">
                {activeFilters.length}
              </span>
            )}
          </button>

          {/* Date range */}
          <div className="flex items-center gap-1">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="h-8 rounded-input border border-sc-border bg-sc-input-bg px-2 text-body-sm text-sc-text"
            />
            <span className="text-body-sm text-sc-text-muted">to</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="h-8 rounded-input border border-sc-border bg-sc-input-bg px-2 text-body-sm text-sc-text"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setLiveUpdates(!liveUpdates)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-btn text-body-sm font-medium transition-colors border ${
              liveUpdates
                ? "bg-sc-success-bg text-sc-success border-sc-success/30"
                : "text-sc-text-muted border-sc-border hover:bg-sc-surface-hover"
            }`}
            title={liveUpdates ? "Live updates active (every 15s) — click to pause" : "Live updates paused — click to resume"}
          >
            {liveUpdates ? (
              <>
                <Radio size={14} className="animate-pulse" />
                Live
              </>
            ) : (
              <>
                <Pause size={14} />
                Paused
              </>
            )}
          </button>
          <button
            onClick={() => handleExport("csv")}
            disabled={exportQuery.isFetching}
            className="flex items-center gap-1 px-3 py-1.5 rounded-btn text-body-sm font-medium text-sc-text border border-sc-border hover:bg-sc-surface-hover transition-colors disabled:opacity-50"
          >
            <Download size={14} />
            CSV
          </button>
          <button
            onClick={() => handleExport("json")}
            disabled={exportQuery.isFetching}
            className="flex items-center gap-1 px-3 py-1.5 rounded-btn text-body-sm font-medium text-sc-text border border-sc-border hover:bg-sc-surface-hover transition-colors disabled:opacity-50"
          >
            <Download size={14} />
            JSON
          </button>
        </div>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <GlassCard>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
                Event Type
              </label>
              <select
                value={eventType}
                onChange={(e) => {
                  setEventType(e.target.value);
                  setPage(1);
                }}
                className="w-full h-8 rounded-input border border-sc-border bg-sc-input-bg px-2 text-body-sm text-sc-text"
              >
                <option value="">All events</option>
                {EVENT_TYPE_GROUPS.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {group.types.map((type) => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, " ")}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
                Severity
              </label>
              <select
                value={severity}
                onChange={(e) => {
                  setSeverity(e.target.value);
                  setPage(1);
                }}
                className="w-full h-8 rounded-input border border-sc-border bg-sc-input-bg px-2 text-body-sm text-sc-text"
              >
                <option value="">All severities</option>
                {SEVERITY_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
                Actor
              </label>
              <input
                type="text"
                value={actor}
                onChange={(e) => {
                  setActor(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by actor name..."
                className="w-full h-8 rounded-input border border-sc-border bg-sc-input-bg px-2 text-body-sm text-sc-text placeholder:text-sc-text-muted"
              />
            </div>
            <div>
              <label className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
                Target
              </label>
              <input
                type="text"
                value={target}
                onChange={(e) => {
                  setTarget(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by target..."
                className="w-full h-8 rounded-input border border-sc-border bg-sc-input-bg px-2 text-body-sm text-sc-text placeholder:text-sc-text-muted"
              />
            </div>
          </div>
        </GlassCard>
      )}

      {/* Active filter chips (AC-05) */}
      {activeFilters.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {activeFilters.map((f) => (
            <span
              key={f.key}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-badge font-medium bg-sc-primary/10 text-sc-primary"
            >
              {f.label}
              <button
                onClick={f.onRemove}
                className="hover:text-sc-primary/70"
                aria-label={`Remove ${f.label} filter`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
          <button
            onClick={() => {
              setEventType("");
              setSeverity("");
              setActor("");
              setTarget("");
              setPage(1);
            }}
            className="text-body-sm text-sc-text-muted hover:text-sc-text"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Data table with expandable rows */}
      <DataTable<AuditLogEntry>
        columns={COLUMNS}
        data={events}
        total={total}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        sort={sort}
        onSortChange={setSort}
        isLoading={auditQuery.isLoading}
        rowKey={(row) => row.id}
        onRowClick={(row) =>
          setExpandedId(expandedId === row.id ? null : row.id)
        }
        expandedRowKey={expandedId}
        renderExpandedRow={(row) => (
          <ExpandedEventDetail
            event={row}
            onClose={() => setExpandedId(null)}
          />
        )}
        emptyState={
          <EmptyState
            icon={<FileText size={48} />}
            heading="No events match your filters"
            description="Try adjusting your date range or removing filters."
          />
        }
      />
    </div>
  );
}

/** Inline expanded event detail row per Story 7 Step 3 */
function ExpandedEventDetail({
  event,
  onClose,
}: {
  readonly event: AuditLogEntry | undefined;
  readonly onClose: () => void;
}) {
  if (!event) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-h3 font-display text-sc-navy">Event Details</h3>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="text-sc-text-muted hover:text-sc-text"
          aria-label="Close details"
        >
          <X size={18} />
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <DetailField label="Event ID" value={event.id} mono />
        <DetailField label="Timestamp" value={formatTimestamp(event.timestamp)} />
        <DetailField label="Event Type" value={event.eventType} />
        <DetailField label="Severity" value={event.severity} />
        <DetailField label="Actor" value={event.actorDisplayName ?? "System"} />
        <DetailField label="Actor ID" value={event.actorUserId ?? "N/A"} mono />
        <DetailField label="Tenant" value={event.tenantName || event.tenantId} />
        <DetailField label="Tenant ID" value={event.tenantId} mono />
        <DetailField label="Target Type" value={event.targetResourceType} />
        <DetailField label="Target ID" value={event.targetResourceId} mono />
        <DetailField label="Target Name" value={event.targetResourceName} />
        <DetailField label="Correlation ID" value={event.correlationId} mono />
        {event.ipAddress && (
          <DetailField label="IP Address" value={event.ipAddress} mono />
        )}
      </div>

      <div className="mb-3">
        <span className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
          Description
        </span>
        <p className="text-body-sm text-sc-text">{event.description}</p>
      </div>

      {Object.keys(event.metadata).length > 0 && (
        <div className="mb-3">
          <span className="text-label text-sc-text-muted uppercase tracking-wider block mb-1">
            Metadata
          </span>
          <pre className="text-label font-mono text-sc-text bg-sc-surface-hover rounded-input p-3 overflow-x-auto max-h-48">
            {JSON.stringify(event.metadata, null, 2)}
          </pre>
        </div>
      )}

      <div className="pt-2 border-t border-sc-border flex items-center gap-4 text-label text-sc-text-muted">
        <span>Hash: <code className="font-mono">{event.hash.slice(0, 16)}...</code></span>
        <span>
          Previous: <code className="font-mono">
            {event.previousHash === "GENESIS"
              ? "GENESIS"
              : `${event.previousHash.slice(0, 16)}...`}
          </code>
        </span>
      </div>
    </div>
  );
}

function DetailField({
  label,
  value,
  mono,
}: {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}) {
  return (
    <div>
      <span className="text-label text-sc-text-muted uppercase tracking-wider block">
        {label}
      </span>
      <span
        className={`text-body-sm text-sc-text break-all ${mono ? "font-mono" : ""}`}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}
